/* The v13 walk card (locked 2026-08-31) — spec of record:
 * mock-lookthrough §1, re-dressed from the shipped L1–L5 walk engine.
 *
 * A decide-stage card (wine — no new color): the chain segment drawn
 * IN the card with the node outlined, the question, TWO options with
 * the follow-up inside the chosen one, ONE Confirm. Stop reasons carry
 * the per-paper split ("accounts ✓ on record · IC memo needed") — a
 * missing paper never blocks the confirm: it books the stop and the
 * paper joins the owed list, chased like any other. Continue never
 * mints a request (chase is the one source). "Leave open" lives under
 * ⋯ and the card re-surfaces. */

import { useMemo, useState } from 'react'
import { ApiError } from '../../lib/api'
import { resolveWalk } from './api'
import type { DeckCard, DeckResponse, WalkOutcome } from './types'
import type { OpenDocPayload } from './RkCard'
import { HelpDot } from '../guidance/Guidance'
import Sentence from '../Sentence'
import '../canon.css'

interface Props {
  card: DeckCard
  deck: DeckResponse
  onDone: (slimText: string) => void
  onOpenDoc: (payload: OpenDocPayload) => void
}

function errDetail(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { detail?: unknown } | undefined
    if (body && typeof body.detail === 'string') return body.detail
    return `${err.status} ${err.statusText}`
  }
  return err instanceof Error ? err.message : 'request failed'
}

export default function RkWalkCard({ card, deck, onDone }: Props) {
  const walk = card.walk
  const [pick, setPick] = useState<'' | 'stop' | 'go'>('')
  const [reasonCode, setReasonCode] = useState('')
  const [note, setNote] = useState('')
  const [menuOpen, setMenuOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const chosen = useMemo(
    () => walk?.stop_reasons.find((r) => r.code === reasonCode) ?? null,
    [walk, reasonCode]
  )
  const ready =
    pick === 'go' ||
    (pick === 'stop' && !!chosen && (!chosen.justification_required || !!note.trim()))

  const rail = deck.rail
  const holder = rail.find((n) => n.role === 'holder')
  const position = rail.find((n) => n.role === 'position')
  const instrument = rail.find((n) => n.role === 'instrument')

  const submit = async () => {
    if (!card.task_id || !ready) return
    const outcome: WalkOutcome = pick === 'go' ? 'walk_through' : (reasonCode as WalkOutcome)
    setBusy(true)
    setError(null)
    try {
      const r = await resolveWalk(card.task_id, outcome, note.trim() || undefined)
      onDone(
        pick === 'go'
          ? r.chain_incomplete
            ? '→ continues — chain incomplete; the request is offered from Chase & Request'
            : '→ continues — the walk extends into the record below'
          : `✓ stops here — ${chosen?.label.toLowerCase() ?? outcome}${
              r.anomaly_raised ? ' · Art 19(9) anomaly report recorded' : ''
            }`
      )
    } catch (err) {
      setError(errDetail(err))
    } finally {
      setBusy(false)
    }
  }

  const leaveOpen = async () => {
    if (!card.task_id) return
    setBusy(true)
    setError(null)
    try {
      await resolveWalk(card.task_id, 'pending_review')
      onDone('left open — it comes back later')
    } catch (err) {
      setError(errDetail(err))
    } finally {
      setBusy(false)
    }
  }

  if (!walk) return null

  return (
    <div className="cnv" data-testid="walk-card" data-card-id={card.card_id} data-stage="walk">
      <div className="cn-back" />
      <div className="cn-card">
        <div className="cn-ctit">
          <span className="k q">Decide</span>
          {walk.context_line ? (
            <span
              style={{ fontSize: 'var(--text-12)', color: 'var(--cn-muted)' }}
              data-testid="walk-context"
            >
              {walk.context_line}
            </span>
          ) : null}
          {/* The walk's sentence rides the ? (audit cut 2: no coach scrim). */}
          <HelpDot k="deck.walk" />
        </div>

        {/* The chain segment, node outlined — the chart replaces the rail. */}
        <div className="cn-chart" data-testid="walk-chart">
          {holder ? (
            <div className="cn-nbox e">
              <div className="nm">{holder.name}</div>
            </div>
          ) : null}
          {holder ? (
            <div className="cn-edge">
              <div className="cn-wire pos" />
              {position?.balance_state ? (
                <div className="cn-elab">
                  <div className="pos">{position.balance_state}</div>
                </div>
              ) : null}
            </div>
          ) : null}
          {instrument ? (
            <>
              <div className="cn-nbox i">
                <div className="nm">{instrument.name}</div>
              </div>
              <div className="cn-edge">
                <div className="cn-wire ins" />
              </div>
            </>
          ) : null}
          <div
            className="cn-nbox e"
            style={{ outline: '2px solid var(--cn-q)', outlineOffset: 2 }}
            data-testid="walk-node"
          >
            <div className="nm">{walk.entity_name}</div>
          </div>
        </div>

        {walk.status_lines?.length ? (
          <div
            style={{ padding: '6px 16px 0', fontSize: 'var(--text-12)', color: 'var(--cn-muted)' }}
            data-testid="walk-status-lines"
          >
            {walk.status_lines.map((l) => (
              <div key={l}>{l}</div>
            ))}
          </div>
        ) : null}

        <div className="cn-qblock">
          <Sentence
            as="div"
            className="qq"
            data-testid="walk-question"
            meaning="walk.question"
            values={{ entity: walk.entity_name }}
          />
          <div className="qw" data-testid="walk-rule">
            {walk.rule_line}
          </div>
        </div>

        {/* Option A — stops here; the reasons live INSIDE it. */}
        <div
          style={{
            margin: '10px 16px 0',
            border: `1.5px solid ${pick === 'stop' ? 'var(--cn-q)' : 'var(--cn-line)'}`,
            borderRadius: 10,
            overflow: 'hidden',
          }}
          className="opt"
        >
          <button
            type="button"
            data-testid="walk-opt-stop"
            onClick={() => setPick('stop')}
            style={{
              display: 'flex',
              gap: 10,
              padding: '9px 12px',
              alignItems: 'baseline',
              cursor: 'pointer',
              fontSize: 'var(--text-13)',
              width: '100%',
              border: 0,
              background: pick === 'stop' ? '#fdf5f8' : '#fff',
              font: 'inherit',
              textAlign: 'left',
            }}
          >
            <Radio on={pick === 'stop'} />
            <span>
              <b>Stops here</b>{' '}
              <small style={{ color: 'var(--cn-muted)' }}>
                — pick the reason; each names the document it rests on
              </small>
            </span>
          </button>
          {pick === 'stop' ? (
            <div data-testid="walk-reasons">
              {walk.stop_reasons.map((r) => (
                <div key={r.code} data-reason={r.code}>
                  <button
                    type="button"
                    onClick={() => setReasonCode(r.code)}
                    title={r.citation}
                    style={{
                      display: 'flex',
                      gap: 10,
                      padding: '6px 12px 6px 36px',
                      borderTop: '1px solid var(--cn-soft)',
                      fontSize: 'var(--text-12)',
                      alignItems: 'baseline',
                      cursor: 'pointer',
                      width: '100%',
                      border: 0,
                      background: 'none',
                      font: 'inherit',
                      textAlign: 'left',
                    }}
                  >
                    <Radio on={reasonCode === r.code} small />
                    <span>
                      <b>{r.label}</b>{' '}
                      <small style={{ color: 'var(--cn-muted)' }}>— {r.detail}</small>
                    </span>
                    <span
                      style={{
                        marginLeft: 'auto',
                        flex: 'none',
                        fontSize: 'var(--text-12)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {(r.papers?.length ? r.papers : null)?.map((p, i) => (
                        <span key={p.label}>
                          {i > 0 ? ' · ' : ''}
                          <span className={p.on_record ? 'have' : 'need'}>
                            {p.on_record ? `${p.label} ✓ on record` : `${p.label} needed`}
                          </span>
                        </span>
                      )) ??
                        (r.evidence_label ? (
                          <span className={r.evidence_on_record ? 'have' : 'need'}>
                            {r.evidence_label}
                          </span>
                        ) : null)}
                    </span>
                  </button>
                  {reasonCode === r.code && r.anomaly ? (
                    <div
                      data-testid="walk-anomaly-band"
                      style={{
                        fontSize: 'var(--text-12)',
                        color: 'var(--cn-gap)',
                        padding: '2px 12px 8px 36px',
                      }}
                    >
                      “No control” files the anomaly report under Art 19(9) with your confirmation.
                    </div>
                  ) : null}
                  {reasonCode === r.code && r.justification_required ? (
                    <div style={{ padding: '2px 12px 8px 36px' }}>
                      <textarea
                        data-testid="walk-justification"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="Your justification — it is the whole content of the decision."
                        style={{
                          width: '100%',
                          minHeight: 44,
                          font: 'inherit',
                          fontSize: 'var(--text-12)',
                          border: '1px solid var(--cn-line)',
                          borderRadius: 8,
                          padding: 8,
                        }}
                      />
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          ) : null}
        </div>

        {/* Option B — continues. */}
        <div
          style={{
            margin: '10px 16px 0',
            border: `1.5px solid ${pick === 'go' ? 'var(--cn-q)' : 'var(--cn-line)'}`,
            borderRadius: 10,
            overflow: 'hidden',
          }}
          className="opt"
        >
          <button
            type="button"
            data-testid="walk-opt-continue"
            onClick={() => setPick('go')}
            style={{
              display: 'flex',
              gap: 10,
              padding: '9px 12px',
              alignItems: 'baseline',
              cursor: 'pointer',
              fontSize: 'var(--text-13)',
              width: '100%',
              border: 0,
              background: pick === 'go' ? '#fdf5f8' : '#fff',
              font: 'inherit',
              textAlign: 'left',
            }}
          >
            <Radio on={pick === 'go'} />
            <span>
              <b>Continues — walk through</b>
            </span>
          </button>
          {pick === 'go' ? (
            <div
              data-testid="walk-consequence"
              style={{
                padding: '6px 12px 9px 36px',
                borderTop: '1px solid var(--cn-soft)',
                fontSize: 'var(--text-12)',
                color: 'var(--cn-muted)',
              }}
            >
              {walk.continue_consequence}
              {walk.continue_leaves_incomplete && walk.chase_hint ? (
                <> The request is offered from {walk.chase_hint} — never minted here.</>
              ) : null}
            </div>
          ) : null}
        </div>

        {ready && pick === 'stop' ? (
          <div
            data-testid="walk-books"
            style={{ padding: '8px 16px 0', fontSize: 'var(--text-12)', color: 'var(--cn-muted)' }}
          >
            Confirm records: terminal — {chosen?.label.toLowerCase()}
            {chosen?.anomaly ? ' · anomaly report raised under Art 19(9)' : ''}
            {chosen?.papers?.some((p) => !p.on_record)
              ? ' · the missing document joins the owed list'
              : ''}
          </div>
        ) : null}
        {error ? <div className="cn-errline">{error}</div> : null}

        <div className="cn-abar">
          <button
            type="button"
            className="cn-btn p"
            style={{ background: 'var(--cn-q)', borderColor: 'var(--cn-q)' }}
            data-testid="walk-confirm"
            disabled={busy || !ready}
            onClick={submit}
          >
            {pick === 'stop' && chosen
              ? `Confirm — stops here, ${chosen.label.toLowerCase()}`
              : pick === 'go'
                ? 'Confirm — continues'
                : 'Confirm'}
          </button>
          <span style={{ position: 'relative' }}>
            <button type="button" className="cn-btn" onClick={() => setMenuOpen((m) => !m)}>
              ⋯
            </button>
            {menuOpen ? (
              <button
                type="button"
                className="cn-btn"
                style={{ marginLeft: 6 }}
                disabled={busy}
                onClick={leaveOpen}
                data-testid="walk-leave-open"
              >
                Leave open — comes back later
              </button>
            ) : null}
          </span>
        </div>
        <div className="cn-note" style={{ padding: '0 16px 12px' }}>
          A missing document does not block the confirm — it books the stop and the document joins
          the owed list, chased like any other.
        </div>
      </div>
    </div>
  )
}

function Radio({ on, small }: { on: boolean; small?: boolean }) {
  const size = small ? 12 : 14
  return (
    <span
      style={{
        width: size,
        height: size,
        border: `1.5px solid ${on ? 'var(--cn-q)' : 'var(--cn-faint)'}`,
        borderRadius: 99,
        flex: 'none',
        position: 'relative',
        top: 2,
        display: 'inline-block',
      }}
    >
      {on ? (
        <span
          style={{
            position: 'absolute',
            inset: 2.5,
            borderRadius: 99,
            background: 'var(--cn-q)',
          }}
        />
      ) : null}
    </span>
  )
}
